import { Button, Spin, Empty } from "antd";
import { FaRegTrashAlt, FaPlus } from "react-icons/fa";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
// import { useTranslation } from "react-i18next";

const AddonsSelect = ({
    exisitingAddons = [],
    setExistingAddons,
    availabeAddons = [],
    setAvailableAddons,
    selectedAddons = [],
    setSelectedAddons,
    isAddonsLoading = false,
}) => {
    const { t } = useTranslation(["reservations"]);

    // Remove addon from existing addons
    const handleRemoveExistingAddon = (addonId) => {
        setAvailableAddons((prev) => [...prev, exisitingAddons.find((addon) => addon.id === addonId)]);
        setExistingAddons((prev) => prev.filter((addon) => addon.id !== addonId));
    };

    // Remove addon from selected addons
    const handleRemoveSelectedAddon = (addonId) => {
        setAvailableAddons((prev) => [...prev, selectedAddons.find((addon) => addon.id === addonId)]);
        setSelectedAddons((prev) => prev.filter((addon) => addon.id !== addonId));
    };

    // Add addon to selected addons
    const handleAddAddon = (addon) => {
        if (!selectedAddons.some((selected) => selected.id === addon.id)) {
            setSelectedAddons((prev) => [...prev, addon]);
            setAvailableAddons((prev) => prev.filter((available) => available.id !== addon.id));
        }
    };

    return (
        <div className="space-y-4 h-full flex flex-col overflow-y-auto">
            {/* 1. Existing Add-ons Section */}
            <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-800 text-left">{t("addons.currentAddons")}</h3>
                <div className="h-44 border rounded-lg bg-gray-50">
                    {isAddonsLoading ? (
                        <div className="h-full flex items-center justify-center">
                            <div className="text-center">
                                <Spin size="default" />
                                <p className="text-gray-500 mt-2 text-sm">{t("addons.loadingExistingAddons")}</p>
                            </div>
                        </div>
                    ) : exisitingAddons.length > 0 ? (
                        <div className="space-y-2 h-full overflow-y-auto p-3">
                            {exisitingAddons.map((addon) => (
                                <AddonCard
                                    key={addon.id}
                                    addon={addon}
                                    type="existing"
                                    onRemove={handleRemoveExistingAddon}
                                    t={t}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="h-full flex items-center justify-center">
                            <EmptyState
                                message={t("addons.noExistingAddons")}
                                description={t("addons.noExistingAddonsDescription")}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* 2. Selected Add-ons Section */}
            <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-800 text-left">{t("addons.selectedAddons")}</h3>
                <div className="h-44 border rounded-lg bg-gray-50">
                    {selectedAddons.length > 0 ? (
                        <div className="space-y-2 h-full overflow-y-auto p-3">
                            {selectedAddons.map((addon) => (
                                <AddonCard
                                    key={addon.id}
                                    addon={addon}
                                    type="selected"
                                    onRemove={handleRemoveSelectedAddon}
                                    t={t}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="h-full flex items-center justify-center">
                            <EmptyState
                                message={t("addons.noSelectedAddons")}
                                description={t("addons.noSelectedAddonsDescription")}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* 3. Available Add-ons Section */}
            <div className="space-y-3 flex-1 min-h-0">
                <h3 className="text-lg font-semibold text-gray-800 text-left">{t("addons.availableAddons")}</h3>
                <div className="h-44 border rounded-lg bg-gray-50 min-h-48">
                    {isAddonsLoading ? (
                        <div className="h-full flex items-center justify-center">
                            <div className="text-center">
                                <Spin size="default" />
                                <p className="text-gray-500 mt-2 text-sm">{t("addons.loadingAvailableAddons")}</p>
                            </div>
                        </div>
                    ) : availabeAddons.length > 0 ? (
                        <div className="space-y-2 h-full overflow-y-auto p-3">
                            {availabeAddons.map((addon) => (
                                <AddonCard key={addon.id} addon={addon} type="available" onAdd={handleAddAddon} t={t} />
                            ))}
                        </div>
                    ) : (
                        <div className="h-full flex items-center justify-center">
                            <EmptyState
                                message={t("addons.noAvailableAddons")}
                                description={t("addons.noAvailableAddonsDescription")}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

AddonsSelect.propTypes = {
    exisitingAddons: PropTypes.array,
    setExistingAddons: PropTypes.func.isRequired,
    availabeAddons: PropTypes.array,
    setAvailableAddons: PropTypes.func.isRequired,
    selectedAddons: PropTypes.array,
    setSelectedAddons: PropTypes.func.isRequired,
    isAddonsLoading: PropTypes.bool,
};

export default AddonsSelect;

// Common addon card component
const AddonCard = ({ addon, type, onRemove, onAdd, t }) => {
    const isUnavailable = type === "existing" && !addon.isAvailableForNewCar;

    return (
        <div className={`flex items-center gap-3 ${isUnavailable ? "opacity-75" : ""}`}>
            <div
                className={`p-3 rounded-lg flex items-center justify-between min-w-0 flex-1 ${
                    isUnavailable
                        ? "border-2 border-red-300 bg-red-50"
                        : type === "selected"
                          ? "bg-blue-100 text-blue-600"
                          : type === "existing"
                            ? "border"
                            : "bg-gray-100 text-gray-700"
                }`}
            >
                <div className="min-w-0 flex-1">
                    <p className={`font-medium truncate ${isUnavailable ? "text-red-600" : ""}`}>{addon.name}</p>
                    {isUnavailable && <p className="text-sm text-red-500 mt-1">{t("addons.addonNotAvailable")}</p>}
                </div>
                <p className={`font-semibold ml-2 ${isUnavailable ? "text-red-600" : ""}`}>
                    ${addon.rate || addon.price || 0}
                    {t("addons.perDay")}
                </p>
            </div>

            {/* Action buttons */}
            {type === "existing" && addon.isAvailableForNewCar && (
                <Button
                    size="small"
                    variant="text"
                    onClick={() => onRemove(addon.id)}
                    className="flex-shrink-0"
                    title={t("addons.removeAddon")}
                >
                    <FaRegTrashAlt color="red" size={16} />
                </Button>
            )}

            {type === "selected" && (
                <Button
                    size="small"
                    variant="text"
                    onClick={() => onRemove(addon.id)}
                    className="flex-shrink-0"
                    title={t("addons.removeAddon")}
                >
                    <FaRegTrashAlt color="red" size={16} />
                </Button>
            )}

            {type === "available" && (
                <Button
                    size="small"
                    type="primary"
                    onClick={() => onAdd(addon)}
                    className="flex-shrink-0"
                    title={t("addons.addAddon")}
                >
                    <FaPlus size={14} />
                </Button>
            )}
        </div>
    );
};

// Empty state component
const EmptyState = ({ message, description }) => (
    <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        styles={{ height: 40 }}
        description={
            <div className="text-center">
                <p className="text-gray-500 text-sm">{message}</p>
                {description && <p className="text-gray-400 text-xs mt-1">{description}</p>}
            </div>
        }
    />
);

// Update PropTypes to include t
AddonCard.propTypes = {
    addon: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
        rate: PropTypes.number,
        price: PropTypes.number,
        isAvailableForNewCar: PropTypes.bool,
    }).isRequired,
    type: PropTypes.oneOf(["existing", "selected", "available"]).isRequired,
    onRemove: PropTypes.func,
    onAdd: PropTypes.func,
    t: PropTypes.func.isRequired,
};

EmptyState.propTypes = {
    message: PropTypes.string.isRequired,
    description: PropTypes.string,
};
